import { useQuery } from '@tanstack/react-query'
import { type SearchMiddleware, useRouter, useSearch } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from 'react'

import { apiFetch } from '@/lib/api'
import { validarBusca } from '@/lib/buscaNaUrl'

/*
 * O recorte: o conjunto de imóveis que está na tela. Lista, mapa e indicadores
 * mostram sempre o mesmo, então ele mora num lugar só — a URL da rota do mapa —
 * e todo mundo lê e escreve por aqui: `useRecorte()` para o recorte e as
 * trocas, `useImoveisDoRecorte()` para a lista dele.
 *
 * Na URL e não num estado do React porque o link do recorte precisa ser
 * compartilhável, recarregar a página tem de reaplicá-lo e o voltar do
 * navegador tem de desfazer um filtro.
 *
 *   /?bioma=4&uf=RJ&municipio=3301009&vcMin=0.8&vcMax=1&recibo=…
 *
 * Valor padrão (todos os biomas, estados e municípios; VC de 0 a 1; sem busca)
 * não vai para a URL: o recorte inteiro é `/`. O decimal da URL usa ponto; a
 * vírgula é só da tela.
 */

/** O recorte já validado. Filtro ausente é "Todos". */
export interface Recorte {
  /** Código do bioma do IBGE. */
  bioma?: number
  /** Sigla da UF, em maiúsculas. */
  uf?: string
  /** Código IBGE do município, de sete algarismos. */
  municipio?: number
  /** Recibo do CAR buscado na barra; nunca junto com o município. */
  recibo?: string
  /** Limites do Valor de Conservação, inclusivos, de 0 a 1, com `vcMin` ≤ `vcMax`. */
  vcMin: number
  vcMax: number
}

/** O recorte como vai para a URL: só o que difere do padrão. */
export interface RecorteNaUrl {
  bioma?: number
  uf?: string
  municipio?: number
  recibo?: string
  vcMin?: number
  vcMax?: number
}

export const RECORTE_PADRAO: Recorte = { vcMin: 0, vcMax: 1 }

const VC_MINIMO = 0
const VC_MAXIMO = 1

/** Quanto o arraste do VC fica parado antes de mudar a URL e pedir a lista. */
export const ESPERA_DO_ARRASTE_MS = 300

/** Um mesmo pedido traz a página inteira que a API permite. */
const TAMANHO_DA_PAGINA = 100

/** A rota do mapa, a única cuja busca é um recorte. */
const ROTA_DO_MAPA = '/'

const SIGLA_DE_UF = /^[A-Za-z]{2}$/
const INTEIRO_POSITIVO = /^[1-9]\d*$/

// ── Leitura e escrita ──────────────────────────────────────────────────────

/** O maior código que a API aceita: ela lê o bioma como inteiro de 32 bits. */
const MAIOR_CODIGO_DE_BIOMA = 2_147_483_647

function lerBioma(bruto: unknown): number | undefined {
  if (typeof bruto !== 'number' && typeof bruto !== 'string') return undefined
  if (!INTEIRO_POSITIVO.test(String(bruto))) return undefined
  // Código maior que o que a API aceita viraria um 400 e o aviso de falha no
  // mapa; como os outros valores sem sentido, ele some do recorte.
  const codigo = Number(bruto)
  return codigo <= MAIOR_CODIGO_DE_BIOMA ? codigo : undefined
}

function lerUf(bruto: unknown): string | undefined {
  return typeof bruto === 'string' && SIGLA_DE_UF.test(bruto) ? bruto.toUpperCase() : undefined
}

function lerVc(bruto: unknown, padrao: number): number {
  const numero =
    typeof bruto === 'number'
      ? bruto
      : typeof bruto === 'string' && bruto.trim() !== ''
        ? Number(bruto)
        : Number.NaN
  if (!Number.isFinite(numero)) return padrao
  // O controle anda de 0,01 em 0,01; um link com mais casas filtraria um
  // recorte que a tela não consegue mostrar nem refazer.
  const noPasso = Math.round(numero * 100) / 100
  return Math.min(VC_MAXIMO, Math.max(VC_MINIMO, noPasso))
}

/** Mínimo acima do máximo leva o máximo até o mínimo; os dois ficam entre 0 e 1. */
function limitarVc(vcMin: number, vcMax: number): Pick<Recorte, 'vcMin' | 'vcMax'> {
  const minimo = lerVc(vcMin, VC_MINIMO)
  const maximo = lerVc(vcMax, VC_MAXIMO)
  return { vcMin: minimo, vcMax: Math.max(minimo, maximo) }
}

/**
 * Lê o recorte de uma URL qualquer. Valor sem sentido some em vez de quebrar a
 * tela: estado fora de duas letras, município fora de sete algarismos, bioma
 * que não é inteiro positivo. VC que não é número vale o padrão, e fora de 0 a 1
 * é levado ao limite. O roteador já converte o que parece número, então os dois
 * formatos entram aqui. Município e recibo seguem a regra da busca: juntos, vale
 * o município.
 */
export function lerRecorte(bruto: Record<string, unknown>): Recorte {
  const { municipio, recibo } = validarBusca(bruto)
  const bioma = lerBioma(bruto.bioma)
  const uf = lerUf(bruto.uf)
  return {
    ...(bioma === undefined ? {} : { bioma }),
    ...(uf === undefined ? {} : { uf }),
    ...(municipio === undefined ? {} : { municipio }),
    ...(recibo === undefined ? {} : { recibo }),
    ...limitarVc(lerVc(bruto.vcMin, VC_MINIMO), lerVc(bruto.vcMax, VC_MAXIMO)),
  }
}

/** O recorte como vai para a URL, sem nada do que é padrão (VC 0 e 1 inclusive). */
export function escreverRecorte(recorte: Recorte): RecorteNaUrl {
  return {
    ...(recorte.bioma === undefined ? {} : { bioma: recorte.bioma }),
    ...(recorte.uf === undefined ? {} : { uf: recorte.uf }),
    ...(recorte.municipio === undefined ? {} : { municipio: recorte.municipio }),
    ...(recorte.recibo === undefined ? {} : { recibo: recorte.recibo }),
    ...(recorte.vcMin === VC_MINIMO ? {} : { vcMin: recorte.vcMin }),
    ...(recorte.vcMax === VC_MAXIMO ? {} : { vcMax: recorte.vcMax }),
  }
}

/**
 * Validação da rota do mapa: dá à tela o recorte tipado. Sozinha ela não limpa
 * a URL — o roteador reescreve só as chaves cujo valor mudou e mantém as que a
 * validação descartou —; quem limpa é {@link canonizarRecorte}.
 */
export function validarRecorte(bruto: Record<string, unknown>): RecorteNaUrl {
  return escreverRecorte(lerRecorte(bruto))
}

/**
 * Middleware de busca da rota do mapa: toda URL que o roteador monta, inclusive
 * a do link aberto (que ele refaz ao montar a tela, com `replace`), sai como o
 * recorte escrito de novo. Assim o link copiado da barra do navegador não leva
 * valor padrão nem parâmetro desconhecido.
 */
export const canonizarRecorte: SearchMiddleware<RecorteNaUrl> = ({ search, next }) =>
  escreverRecorte(lerRecorte(next(search) as Record<string, unknown>))

// ── Regras de troca ────────────────────────────────────────────────────────

function sem<K extends keyof Recorte>(recorte: Recorte, ...chaves: K[]): Recorte {
  const copia = { ...recorte }
  for (const chave of chaves) delete copia[chave]
  return copia
}

/** Trocar o estado limpa o município: o escolhido pode não ser do estado novo. */
export function trocarUf(recorte: Recorte, uf: string | undefined): Recorte {
  const resto = sem(recorte, 'uf', 'municipio')
  return uf === undefined ? resto : { ...resto, uf }
}

/**
 * Escolher um município faz do estado dele o estado do recorte, e o município
 * toma o lugar do recibo que estivesse na barra. Voltar a "Todos" mantém o
 * estado.
 */
export function escolherMunicipio(
  recorte: Recorte,
  municipio: { codigo: number; uf: string } | undefined
): Recorte {
  if (municipio === undefined) return sem(recorte, 'municipio')
  return { ...sem(recorte, 'recibo'), uf: municipio.uf, municipio: municipio.codigo }
}

/**
 * Trocar o bioma mantém estado e município: as opções de estado e município não
 * se estreitam pelo bioma, e combinação sem imóvel cai no aviso de recorte vazio.
 */
export function trocarBioma(recorte: Recorte, bioma: number | undefined): Recorte {
  const resto = sem(recorte, 'bioma')
  return bioma === undefined ? resto : { ...resto, bioma }
}

// ── Arraste do VC ──────────────────────────────────────────────────────────

type LimitesDoVc = Pick<Recorte, 'vcMin' | 'vcMax'>

/**
 * O arraste do VC que ainda espera a pausa para ir à URL: o valor da mão e o
 * temporizador. Um por roteador, compartilhado por todo `useRecorte()` da tela.
 */
function criarArraste() {
  let pendente: LimitesDoVc | null = null
  let esperando: LimitesDoVc | undefined
  let temporizador: ReturnType<typeof setTimeout> | undefined
  let contagem = 0
  let dono: object | undefined
  const ouvintes = new Set<() => void>()
  const avisar = () => ouvintes.forEach((ouvinte) => ouvinte())

  const arraste = {
    assinar: (ouvinte: () => void) => {
      ouvintes.add(ouvinte)
      return () => void ouvintes.delete(ouvinte)
    },
    pendente: () => pendente,
    esperando: () => esperando,
    atual: () => contagem,
    dono: () => dono,
    definirPendente(vc: LimitesDoVc | null) {
      if (pendente === vc) return
      pendente = vc
      avisar()
    },
    cancelarEspera() {
      clearTimeout(temporizador)
      temporizador = undefined
      esperando = undefined
    },
    /** A pausa acabou: o valor segue para a URL e deixa de esperar. */
    encerrarEspera() {
      temporizador = undefined
      esperando = undefined
    },
    definirEspera(novo: ReturnType<typeof setTimeout>) {
      temporizador = novo
    },
    /** Começa um arraste (ou continua o em curso) com este valor; devolve o número dele. */
    novoArraste(vc: LimitesDoVc, quem: object) {
      contagem += 1
      dono = quem
      arraste.definirPendente(vc)
      arraste.cancelarEspera()
      esperando = vc
      return contagem
    },
    /** Abandona o arraste: nada dele vai à URL, nem agora nem depois. */
    descartar() {
      arraste.cancelarEspera()
      contagem += 1
      arraste.definirPendente(null)
    },
  }
  return arraste
}

const arrastesPorRoteador = new WeakMap<object, ReturnType<typeof criarArraste>>()

function lojaDoArraste(roteador: object) {
  let arraste = arrastesPorRoteador.get(roteador)
  if (arraste === undefined) {
    arraste = criarArraste()
    arrastesPorRoteador.set(roteador, arraste)
  }
  return arraste
}

// ── Hooks ──────────────────────────────────────────────────────────────────

function useRecorteDaUrl(): Recorte {
  const buscaDaUrl: Record<string, unknown> = useSearch({ strict: false })
  const recorte = lerRecorte(buscaDaUrl)
  // Mesma URL, mesmo objeto: quem depende do recorte num efeito não roda à toa.
  const chave = JSON.stringify(escreverRecorte(recorte))
  // eslint-disable-next-line react-hooks/exhaustive-deps -- a chave já resume o recorte
  return useMemo(() => recorte, [chave])
}

export interface UsoDoRecorte {
  /** O recorte da URL, o que lista, mapa e indicadores mostram. */
  recorte: Recorte
  /**
   * Os limites do VC para o controle desenhar: durante o arraste, o valor da
   * mão, antes de ir para a URL; parado, o do recorte.
   */
  vcNaTela: Pick<Recorte, 'vcMin' | 'vcMax'>
  trocarBioma: (bioma: number | undefined) => void
  trocarUf: (uf: string | undefined) => void
  escolherMunicipio: (municipio: { codigo: number; uf: string } | undefined) => void
  /** Muda o VC depois de {@link ESPERA_DO_ARRASTE_MS} parado: o arraste não vira uma navegação por passo. */
  mudarVc: (vcMin: number, vcMax: number) => void
  /** Volta ao recorte inteiro (URL `/`), descartando o arraste que ainda esperava. */
  limpar: () => void
}

/**
 * O único ponto de leitura e escrita do recorte. Cada troca é uma navegação
 * nova na rota do mapa — o voltar do navegador desfaz —, aplicada sobre a URL
 * do momento, para que duas trocas seguidas não se apaguem.
 */
export function useRecorte(): UsoDoRecorte {
  const recorte = useRecorteDaUrl()
  const router = useRouter()

  // Depende só do roteador, que vive o app inteiro: o efeito de desmonte abaixo
  // depende dela e não pode rodar a limpeza sem haver desmonte.
  const aplicar = useCallback(
    (regra: (atual: Recorte) => Recorte) =>
      router.navigate({
        to: ROTA_DO_MAPA,
        search: (anterior: Record<string, unknown>) => escreverRecorte(regra(lerRecorte(anterior))),
      }),
    [router]
  )

  // O arraste do VC é de todos os usos do recorte na tela, e não de cada um:
  // quem arrasta é o filtro de valor, mas quem limpa é o botão Limpar dos filtros
  // ou o x da barra, e cada um chama `useRecorte()` à parte. Se a espera fosse de
  // cada uso, o Limpar não alcançaria o arraste do outro e o valor da mão voltaria
  // à URL depois da limpeza.
  const arraste = lojaDoArraste(router)
  const vcPendente = useSyncExternalStore(arraste.assinar, arraste.pendente)
  // Quem arrastou por último é quem leva o valor à URL se for desmontado.
  const dono = useRef({})

  // Voltar ou avançar no navegador, ou sair da rota do mapa, durante a espera:
  // a pessoa foi para outra URL, e o VC da mão não pode reescrevê-la.
  useEffect(
    () =>
      router.history.subscribe(({ location, action }) => {
        if (arraste.esperando() === undefined) return
        if (action.type === 'PUSH' || action.type === 'REPLACE') {
          if (location.pathname === ROTA_DO_MAPA) return
        }
        arraste.descartar()
      }),
    [router, arraste]
  )

  // Fechar a gaveta dos filtros logo depois de soltar o controle desmonta quem
  // o usa, e a pessoa continua no mapa: o último valor vai para a URL em vez de
  // se perder sem aviso. Fora do mapa, ele é descartado.
  useEffect(() => {
    const eu = dono.current
    return () => {
      if (arraste.dono() !== eu) return
      const vc = arraste.esperando()
      // Sem espera, o temporizador já levou o valor e ele mesmo solta o da mão.
      if (vc === undefined) return
      if (router.history.location.pathname !== ROTA_DO_MAPA) {
        arraste.descartar()
        return
      }
      arraste.cancelarEspera()
      const este = arraste.atual()
      // Como no fim da pausa: com o valor na URL, o controle volta a seguir a
      // URL. Sem isso, um voltar do navegador depois deixaria o valor da mão
      // preso na tela, porque o voltar não passa pelo Limpar.
      void aplicar((atual) => ({ ...atual, ...vc })).then(() => {
        if (arraste.atual() === este) arraste.definirPendente(null)
      })
    }
  }, [aplicar, router, arraste])

  const mudarVc = useCallback(
    (vcMin: number, vcMax: number) => {
      const vc = limitarVc(vcMin, vcMax)
      const este = arraste.novoArraste(vc, dono.current)
      arraste.definirEspera(
        setTimeout(() => {
          arraste.encerrarEspera()
          void aplicar((atual) => ({ ...atual, ...vc })).then(() => {
            // Outro arraste começou enquanto a URL mudava: o valor da mão é o dele.
            if (arraste.atual() === este) arraste.definirPendente(null)
          })
        }, ESPERA_DO_ARRASTE_MS)
      )
    },
    [aplicar, arraste]
  )

  const limpar = useCallback(() => {
    arraste.descartar()
    void aplicar(() => RECORTE_PADRAO)
  }, [aplicar, arraste])

  return {
    recorte,
    vcNaTela: vcPendente ?? { vcMin: recorte.vcMin, vcMax: recorte.vcMax },
    trocarBioma: useCallback(
      (bioma: number | undefined) => void aplicar((atual) => trocarBioma(atual, bioma)),
      [aplicar]
    ),
    trocarUf: useCallback(
      (uf: string | undefined) => void aplicar((atual) => trocarUf(atual, uf)),
      [aplicar]
    ),
    escolherMunicipio: useCallback(
      (municipio: { codigo: number; uf: string } | undefined) =>
        void aplicar((atual) => escolherMunicipio(atual, municipio)),
      [aplicar]
    ),
    mudarVc,
    limpar,
  }
}

// ── Lista do recorte ───────────────────────────────────────────────────────

interface Codigo {
  codigo: number
  nome: string
}

/** Um imóvel da lista, como `GET /v1/imoveis` o devolve. */
export interface ItemDoRecorte {
  recibo: string
  municipio: Codigo
  uf: string
  bioma: Codigo
  areaHa: number
  /** O valor gravado, sem recálculo nem arredondamento. */
  valorConservacao: number
  faixa: Codigo | null
}

/** Extensão em graus (SIRGAS 2000): oeste, sul, leste, norte. */
export type Extensao = [oeste: number, sul: number, leste: number, norte: number]

export interface ListaDoRecorte {
  /** Só os imóveis desta página; o recorte inteiro está em `total`. */
  itens: ItemDoRecorte[]
  /** Quantos imóveis o recorte tem, em todas as páginas. */
  total: number
  pagina: number
  tamanho: number
  /** Extensão de todo o recorte, não só da página; `null` quando não há imóvel. */
  bbox: Extensao | null
}

/**
 * Caminho da lista de um recorte. VC padrão não filtra nada e fica de fora; com
 * um dos limites mudado, vão os dois, inclusivos.
 */
export function caminhoDaLista(recorte: Recorte, pagina = 1): string {
  const parametros = new URLSearchParams()
  if (recorte.bioma !== undefined) parametros.set('bioma', String(recorte.bioma))
  if (recorte.uf !== undefined) parametros.set('uf', recorte.uf)
  if (recorte.municipio !== undefined) parametros.set('municipio', String(recorte.municipio))
  if (recorte.vcMin !== VC_MINIMO || recorte.vcMax !== VC_MAXIMO) {
    parametros.set('vcMin', String(recorte.vcMin))
    parametros.set('vcMax', String(recorte.vcMax))
  }
  if (recorte.recibo !== undefined) parametros.set('recibo', recorte.recibo)
  parametros.set('pagina', String(pagina))
  parametros.set('tamanho', String(TAMANHO_DA_PAGINA))
  return `/v1/imoveis?${parametros.toString()}`
}

/**
 * Uma página da lista do recorte da URL (a primeira, se nada for dito): os
 * itens da página, de até {@link TAMANHO_DA_PAGINA} imóveis, e o total e a
 * extensão do recorte inteiro. Só faz sentido na rota do mapa: noutra rota a
 * busca da URL é de outra página e não é um recorte. A chave da consulta é o
 * recorte com a página, então uma resposta de um recorte que já saiu da tela fica no cache
 * dele e não aparece no atual, e vários leitores do mesmo recorte fazem um só
 * pedido.
 */
export function useImoveisDoRecorte(pagina = 1) {
  return useQuery(consultaDaLista(useRecorteDaUrl(), pagina))
}

/**
 * A consulta da lista de um recorte, para quem precisa dela fora da URL atual
 * ou com outra condição de disparo: mesma chave, mesmo pedido no cache. O
 * pedido de um recorte que a pessoa já trocou é cancelado.
 */
export function consultaDaLista(recorte: Recorte, pagina = 1) {
  const caminho = caminhoDaLista(recorte, pagina)
  return {
    queryKey: ['imoveis-do-recorte', caminho] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) => apiFetch<ListaDoRecorte>(caminho, { signal }),
  }
}
